import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import type { MfaFactorSummary } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { mfaApi } from '@/services/mfa.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

/**
 * Managing your own second factors.
 *
 * No permission gate beyond a valid session: this is entirely self-service,
 * the same way changing your own password is.
 */
export function MfaSettingsPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [addingTotp, setAddingTotp] = useState(false);
  const [addingSms, setAddingSms] = useState(false);
  const [backupCodes, setBackupCodes] = useState<readonly string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['mfa', 'factors'], queryFn: () => mfaApi.listFactors() });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['mfa', 'factors'] });
  };

  const enrollEmail = useMutation({
    mutationFn: () => mfaApi.enrollEmail(),
    onSuccess: async (result) => {
      if (result.backupCodes) setBackupCodes(result.backupCodes);
      await refresh();
    },
  });

  const factors = query.data ?? [];
  const hasEmail = factors.some((f) => f.type === 'EMAIL');

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('mfa.settings.title')}</h1>
      </header>

      <p className="field-hint">{t('mfa.settings.description')}</p>

      {notice && <Alert tone="success">{notice}</Alert>}
      {enrollEmail.isError && <Alert tone="danger">{describeError(enrollEmail.error, t)}</Alert>}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && factors.length === 0 && <EmptyState title={t('mfa.settings.empty')} />}

      {factors.length > 0 && (
        <ul className="field-list">
          {factors.map((factor) => (
            <FactorRow key={factor.id} factor={factor} onChanged={refresh} />
          ))}
        </ul>
      )}

      <div className="mfa-add-actions">
        <Button variant="secondary" onClick={() => setAddingTotp(true)}>
          {t('mfa.settings.addTotp')}
        </Button>
        {!hasEmail && (
          <Button
            variant="secondary"
            loading={enrollEmail.isPending}
            onClick={() => enrollEmail.mutate()}
          >
            {t('mfa.settings.addEmail')}
          </Button>
        )}
        <Button variant="secondary" onClick={() => setAddingSms(true)}>
          {t('mfa.settings.addSms')}
        </Button>
      </div>

      {factors.length > 0 && <RegenerateBackupCodes onIssued={(codes) => setBackupCodes(codes)} />}

      {addingTotp && (
        <TotpEnrollDialog
          onClose={() => setAddingTotp(false)}
          onEnrolled={(codes) => {
            setAddingTotp(false);
            if (codes) setBackupCodes(codes);
            setNotice(t('mfa.settings.factorAdded'));
            void refresh();
          }}
        />
      )}

      {addingSms && (
        <SmsEnrollDialog
          onClose={() => setAddingSms(false)}
          onEnrolled={(codes) => {
            setAddingSms(false);
            if (codes) setBackupCodes(codes);
            setNotice(t('mfa.settings.factorAdded'));
            void refresh();
          }}
        />
      )}

      {backupCodes && (
        <BackupCodesDialog codes={backupCodes} onClose={() => setBackupCodes(null)} />
      )}
    </div>
  );
}

// --- Pieces ------------------------------------------------------------------

function FactorRow({
  factor,
  onChanged,
}: {
  factor: MfaFactorSummary;
  onChanged: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const disable = useMutation({
    mutationFn: () => mfaApi.disableFactor(factor.id, password),
    onSuccess: async () => {
      setConfirming(false);
      await onChanged();
    },
    onError: (caught: unknown) => {
      setError(describeError(caught, t));
    },
  });

  return (
    <li>
      <div className="page-header">
        <span>
          <strong>{t(`mfa.factorType.${factor.type}`)}</strong>
          {factor.maskedPhoneNumber && (
            <span className="cell-empty"> · {factor.maskedPhoneNumber}</span>
          )}
          {!factor.verifiedAt && <span className="cell-empty"> · {t('mfa.settings.pending')}</span>}
        </span>
        {!confirming && (
          <Button variant="ghost" onClick={() => setConfirming(true)}>
            {t('mfa.settings.remove')}
          </Button>
        )}
      </div>

      {confirming && (
        <form
          className="inline-confirm"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            disable.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}
          <Field
            label={t('mfa.settings.confirmPassword')}
            name="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoFocus
          />
          <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="danger" loading={disable.isPending}>
            {t('mfa.settings.remove')}
          </Button>
        </form>
      )}
    </li>
  );
}

function RegenerateBackupCodes({ onIssued }: { onIssued: (codes: readonly string[]) => void }) {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const regenerate = useMutation({
    mutationFn: () => mfaApi.regenerateBackupCodes(password),
    onSuccess: (result) => {
      setConfirming(false);
      setPassword('');
      onIssued(result.codes);
    },
    onError: (caught: unknown) => setError(describeError(caught, t)),
  });

  if (!confirming) {
    return (
      <p>
        <Button variant="ghost" onClick={() => setConfirming(true)}>
          {t('mfa.settings.regenerateBackupCodes')}
        </Button>
      </p>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        regenerate.mutate();
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <p>{t('mfa.settings.regenerateBackupCodesConfirm')}</p>
      <Field
        label={t('mfa.settings.confirmPassword')}
        name="password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="primary" loading={regenerate.isPending}>
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

function TotpEnrollDialog({
  onClose,
  onEnrolled,
}: {
  onClose: () => void;
  onEnrolled: (backupCodes: readonly string[] | null) => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const enroll = useMutation({
    mutationFn: () => mfaApi.enrollTotp(),
    onSuccess: async (result) => {
      setCredentialId(result.credentialId);
      setSecret(result.secret);
      setQrDataUrl(await QRCode.toDataURL(result.provisioningUri));
    },
    onError: (caught: unknown) => setError(describeError(caught, t)),
  });

  const confirm = useMutation({
    mutationFn: () => mfaApi.confirmTotp(credentialId!, code),
    onSuccess: (result) => onEnrolled(result.backupCodes),
    onError: (caught: unknown) => setError(describeError(caught, t)),
  });

  // Enrollment is started once, when the dialog opens.
  useEffect(() => {
    dialogRef.current?.showModal();
    enroll.mutate();
  }, []);

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="totp-title">
      <div className="dialog-body">
        <h2 id="totp-title">{t('mfa.settings.addTotp')}</h2>

        {error && <Alert tone="danger">{error}</Alert>}

        {enroll.isPending && <Spinner label={t('common.states.loading')} />}

        {qrDataUrl && (
          <>
            <p>{t('mfa.totp.scanHint')}</p>
            <img src={qrDataUrl} alt={t('mfa.totp.qrAlt')} width={200} height={200} />
            {secret && (
              <p className="field-hint" dir="ltr">
                {t('mfa.totp.manualEntry')}: <code>{secret}</code>
              </p>
            )}

            <form
              onSubmit={(event) => {
                event.preventDefault();
                setError(null);
                confirm.mutate();
              }}
            >
              <Field
                label={t('mfa.challenge.code')}
                name="code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
                autoFocus
                dir="ltr"
              />
              <footer className="dialog-actions">
                <Button type="button" variant="ghost" onClick={onClose}>
                  {t('common.actions.cancel')}
                </Button>
                <Button type="submit" variant="primary" loading={confirm.isPending}>
                  {t('mfa.challenge.verify')}
                </Button>
              </footer>
            </form>
          </>
        )}
      </div>
    </dialog>
  );
}

function SmsEnrollDialog({
  onClose,
  onEnrolled,
}: {
  onClose: () => void;
  onEnrolled: (backupCodes: readonly string[] | null) => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const enroll = useMutation({
    mutationFn: () => mfaApi.enrollSms(phoneNumber),
    onSuccess: (result) => setCredentialId(result.credentialId),
    onError: (caught: unknown) => setError(describeError(caught, t)),
  });

  const confirm = useMutation({
    mutationFn: () => mfaApi.confirmSms(credentialId!, code),
    onSuccess: (result) => onEnrolled(result.backupCodes),
    onError: (caught: unknown) => setError(describeError(caught, t)),
  });

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="sms-title">
      <div className="dialog-body">
        <h2 id="sms-title">{t('mfa.settings.addSms')}</h2>

        {error && <Alert tone="danger">{error}</Alert>}

        {!credentialId ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              enroll.mutate();
            }}
          >
            <Field
              label={t('mfa.sms.phoneNumber')}
              hint={t('mfa.sms.phoneNumberHint')}
              name="phoneNumber"
              type="tel"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
              required
              autoFocus
              dir="ltr"
            />
            <footer className="dialog-actions">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.actions.cancel')}
              </Button>
              <Button type="submit" variant="primary" loading={enroll.isPending}>
                {t('mfa.sms.sendCode')}
              </Button>
            </footer>
          </form>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              confirm.mutate();
            }}
          >
            <Field
              label={t('mfa.challenge.code')}
              name="code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
              autoFocus
              dir="ltr"
            />
            <footer className="dialog-actions">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.actions.cancel')}
              </Button>
              <Button type="submit" variant="primary" loading={confirm.isPending}>
                {t('mfa.challenge.verify')}
              </Button>
            </footer>
          </form>
        )}
      </div>
    </dialog>
  );
}

function BackupCodesDialog({ codes, onClose }: { codes: readonly string[]; onClose: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="backup-codes-title"
    >
      <div className="dialog-body">
        <h2 id="backup-codes-title">{t('mfa.backupCodes.title')}</h2>
        <Alert tone="warning">{t('mfa.backupCodes.hint')}</Alert>
        <ul className="field-list" dir="ltr">
          {codes.map((code) => (
            <li key={code}>
              <code>{code}</code>
            </li>
          ))}
        </ul>
        <footer className="dialog-actions">
          <Button type="button" variant="primary" onClick={onClose}>
            {t('mfa.backupCodes.done')}
          </Button>
        </footer>
      </div>
    </dialog>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
